// Planche avant/après du rendu premium (outil de développement, aucune donnée de jeu) :
//   /?sheet=rendu   à gauche le dessin d'avant, à droite le même tank cuit avec l'ombrage premium et son ombre portée,
//                   comme en partie. Sert à faire valider le rendu par Léni.
import { useEffect, useRef } from 'react';
import { Application, Container, Graphics, Sprite, Text } from 'pixi.js';
import { getClass, type ClassNode } from '../../../shared/catalog/classes';
import { STYLE_CODE } from '../../../shared/catalog/variants';
import { DEFAULT_LOOK, type PartId, type TankLook } from '../../../shared/cosmetics';
import { ACCENT_ENEMY, ACCENT_SELF, ART_R } from '../game/render/art';
import { PaintCache, type BakedTank } from '../game/render/bake';
import { drawTankParts } from '../game/render/variants';

const W = 1440;

function label(stage: Container, s: string, x: number, y: number, size = 12, color = 0xdfe6ee): void {
  const t = new Text({ text: s, style: { fontFamily: 'system-ui', fontSize: size, fill: color, align: 'center', fontWeight: size > 14 ? '700' : '500' } });
  t.anchor.set(0.5, 0);
  t.position.set(x, y);
  stage.addChild(t);
}

function oldTank(stage: Container, cls: ClassNode, code: number, accent: number, x: number, y: number, size: number, aim: number): void {
  const root = new Container();
  const parts = { hull: new Graphics(), turret: new Graphics(), auto: new Graphics() };
  drawTankParts(parts, cls, code, accent);
  parts.hull.rotation = -Math.PI / 2;
  parts.turret.rotation = aim;
  root.addChild(parts.hull, parts.turret, parts.auto);
  root.position.set(x, y);
  root.scale.set(size / ART_R);
  stage.addChild(root);
}

function newTank(stage: Container, b: BakedTank, x: number, y: number, size: number, aim: number): void {
  const root = new Container();
  const sp = (p: BakedTank['hull'], rot: number, alpha = 1, dx = 0, dy = 0) => {
    const s = new Sprite(p.texture);
    s.anchor.set(p.ax, p.ay);
    s.rotation = rot;
    s.alpha = alpha;
    s.position.set(dx, dy);
    root.addChild(s);
  };
  sp(b.hullShadow, -Math.PI / 2, 0.42, 2.2, 3.2);
  sp(b.turretShadow, aim, 0.42, 2.2, 3.2);
  sp(b.hull, -Math.PI / 2);
  sp(b.turret, aim);
  if (b.hasAuto) {
    const g = new Graphics(b.auto);
    g.rotation = aim + Math.PI / 3;
    root.addChild(g);
  }
  root.position.set(x, y);
  root.scale.set(size / ART_R);
  stage.addChild(root);
}

function look(parts: Partial<Record<PartId, Partial<TankLook['parts'][PartId]>>>): TankLook {
  const out: TankLook = { ...DEFAULT_LOOK, parts: { ...DEFAULT_LOOK.parts } };
  for (const k of Object.keys(parts) as PartId[]) out.parts[k] = { ...DEFAULT_LOOK.parts[k], ...parts[k] };
  return out;
}

export function RenderSheet() {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const host = ref.current;
    if (!host) return;
    const app = new Application();
    let destroyed = false;
    void app.init({ width: W, height: 1240, background: 0x1a1f26, antialias: true, resolution: Math.min(2, window.devicePixelRatio || 1) }).then(() => {
      if (destroyed) return;
      app.canvas.style.width = '100%';
      app.canvas.style.height = 'auto';
      host.appendChild(app.canvas);
      const st = app.stage;
      // Sol quadrillé, comme en partie.
      const ground = new Graphics();
      for (let x = 0; x <= W; x += 40) ground.moveTo(x, 0).lineTo(x, 1240).stroke({ width: 1, color: 0x232a33 });
      for (let y = 0; y <= 1240; y += 40) ground.moveTo(0, y).lineTo(W, y).stroke({ width: 1, color: 0x232a33 });
      st.addChild(ground);
      const cache = new PaintCache(app.renderer);
      label(st, 'Rendu des tanks : avant (à gauche) / après (à droite)', W / 2, 14, 20, 0xffffff);
      label(st, 'Ombrage cuit une fois par apparence (aucun coût par image en partie) ; ombre portée toujours vers le bas à droite.', W / 2, 42, 12, 0x93a1b0);
      const rows: [string, number, number][] = [
        ['BASE', STYLE_CODE.atelier, ACCENT_SELF],
        ['C01-A1', STYLE_CODE.desert, ACCENT_ENEMY],
        ['C06-F', STYLE_CODE.boreal, ACCENT_SELF],
        ['C12-F', STYLE_CODE.obsidienne, ACCENT_ENEMY],
        ['C03-F', STYLE_CODE.signal, ACCENT_SELF],
        ['C09-B', STYLE_CODE.atelier, ACCENT_ENEMY],
      ];
      const colW = W / 3;
      rows.forEach(([id, code, accent], i) => {
        const cls = getClass(id);
        const cx = (i % 3) * colW + colW / 2;
        const cy = 170 + Math.floor(i / 3) * 290;
        oldTank(st, cls, code, accent, cx - 110, cy, 56, -Math.PI / 4);
        newTank(st, cache.getClassic(cls, code, accent, 0), cx + 110, cy, 56, -Math.PI / 4);
        label(st, `${cls.name} — avant`, cx - 110, cy + 110, 12, 0x93a1b0);
        label(st, `${cls.name} — après`, cx + 110, cy + 110, 12, 0xdfe6ee);
      });
      label(st, 'Tanks peints (atelier), rendu premium', W / 2, 760, 16, 0xffffff);
      const painted: [string, TankLook, number][] = [
        ['C01-A1', look({ hull: { color: 0x1c1f24, mat: 'mat:carbone', pat: 'pat:flammes', patColor: 0xff5a1f }, turret: { color: 0x22262c, mat: 'mat:carbone' }, barrels: { color: 0xb9c2cc, mat: 'mat:chrome' }, treads: { color: 0x15171b } }), ACCENT_SELF],
        ['C12-F', look({ hull: { color: 0xc9a227, mat: 'mat:or' }, turret: { color: 0xc9a227, mat: 'mat:or' }, barrels: { color: 0x2b2b2b, mat: 'mat:brosse' }, treads: { color: 0x1e1e1e, dec: 'dec:couronne', decColor: 0xffd166 } }), ACCENT_ENEMY],
        ['C05-F', look({ hull: { color: 0x00e5ff, mat: 'mat:neon', pat: 'pat:circuit', patColor: 0x00e5ff }, turret: { color: 0xff00c8, mat: 'mat:neon' }, barrels: { color: 0x00e5ff, mat: 'mat:neon' }, treads: { color: 0x7a00ff, mat: 'mat:neon' } }), ACCENT_SELF],
        ['C03-F', look({ hull: { color: 0x4f6d3a, pat: 'pat:camo', patColor: 0x2e3b22 }, turret: { color: 0x4f6d3a, pat: 'pat:camo', patColor: 0x2e3b22 }, barrels: { color: 0x3d4f2c }, treads: { color: 0x2a2f22, dec: 'dec:chevrons', decColor: 0xe9d8a6 } }), ACCENT_ENEMY],
        ['C09-B', look({ hull: { color: 0xff5a1f, mat: 'mat:magma' }, turret: { color: 0xff5a1f, mat: 'mat:magma' }, barrels: { color: 0x2a1d18, mat: 'mat:carbone' }, treads: { color: 0x1d1512, dec: 'dec:dragon', decColor: 0xffb703 } }), ACCENT_SELF],
      ];
      painted.forEach(([id, l, accent], i) => {
        const x = W / 10 + i * (W / 5);
        newTank(st, cache.get(getClass(id), l, accent), x, 930, 58, -Math.PI / 3);
        label(st, getClass(id).name, x, 1040, 12);
      });
      app.render();
    });
    return () => {
      destroyed = true;
      app.destroy({ removeView: true });
    };
  }, []);
  return <div ref={ref} style={{ overflow: 'auto', height: '100%' }} data-testid="render-sheet" />;
}
